// @flow

import * as React from 'react';
import { NavigableList } from '@ta/list.navigable-list';
import { localize } from '@ta/common.i18n';
import {
  type TypeaheadResult,
  Selector,
  TypeaheadJsonFetcher,
  TypeaheadJsonResult,
} from '@ta/common.typeahead';
import { CommentBox } from '@ta/iap-reporting.core';
import ButtonGroup from '@ta/social.button-group';
import Button from '@ta/styleguide.button';

import styles from './SuggestionLocationBox.less';

/**
 * Location object to describe a location
 */
export type Location = {|
  /**
   * Location id
   */
  id: number,
  /**
   * Localized location label
   */
  label: string,
|};

const LocationKnownValues = {
  default: 'default',
  yes: 'yes',
  no: 'no',
};

type LocationKnownType = $Keys<typeof LocationKnownValues>;

type Props = {|
  hintText: string,
  comment: string,
  onCommentChange?: (SyntheticKeyboardEvent<HTMLTextAreaElement>) => void,
  location: Location,
  onLocationChange: (number, string) => void,
  onYes: any => void,
  onNo: any => void,
|};

type State = {
  locationKnown: LocationKnownType,
  textInputValue: string,
};

class SuggestLocationBox extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      locationKnown: LocationKnownValues.default,
      textInputValue: props.location.label,
    };
  }

  onYesClick = () => {
    this.setState({ locationKnown: LocationKnownValues.yes });
    this.props.onYes();
  };

  onNoClick = () => {
    this.setState({ locationKnown: LocationKnownValues.no });
    this.props.onNo();
  };

  onResultSelected = (selectedResult: TypeaheadResult) => {
    const displayValue = selectedResult.displayValue || '';
    this.props.onLocationChange(parseInt(selectedResult.value, 10), displayValue);
    this.setState({ textInputValue: displayValue });
  };

  textInputChanged = (newValue: string) => {
    this.setState({ textInputValue: newValue });
  };

  typeaheadJsonFetcher = new TypeaheadJsonFetcher({
    interleaved: true,
    types: ['hotel', 'attr', 'eat', 'act'],
    filter: ['nobroad'],
    name_depth: 1,
    details: true,
    legacy_format: true,
    urlList: false,
    max: 25,
  });

  defaultResult: TypeaheadResult = {
    type: 'location_id',
    value: '0',
  };

  render = () => {
    const yesType = this.state.locationKnown === LocationKnownValues.yes ? 'primary' : 'secondary';
    const noType = this.state.locationKnown === LocationKnownValues.no ? 'primary' : 'secondary';

    return (
      <div>
        <div className={styles.doYouKnow}>{this.props.hintText}</div>

        <ButtonGroup>
          <Button type={yesType} onClick={this.onYesClick}>
            {localize('common_Yes')}
          </Button>
          <Button type={noType} onClick={this.onNoClick}>
            {localize('common_No')}
          </Button>
        </ButtonGroup>

        <div
          style={{
            display: this.state.locationKnown === LocationKnownValues.yes ? 'block' : 'none',
          }}
        >
          <div className={styles.enterName}>{localize('social_report_level3_1')}</div>

          <div className={styles.selectorWrap}>
            {/*
              I looked into making Selector a controlled component but it got dicey
              By using a key, we force a refresh whenever the location id changes.
              Since it uses the display value of the prop location, this helps us clear the field when needed
              but keeps the display value between re-renders like you would expect.
              Seen here: https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
              If this proves to be a common use case, this may be better: https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#alternative-1-reset-uncontrolled-component-with-an-id-prop
           */}
            <Selector
              typeaheadTextInputValue={this.state.textInputValue}
              selectionChanged={this.onResultSelected}
              fetchers={[this.typeaheadJsonFetcher]}
              textInputChanged={this.textInputChanged}
              placeHolder={localize('common_25f1')}
              key={this.props.location.id}
              closeDropdownOnClickOutside={false}
            >
              {(results, tracker, onSelection) => (
                <div className={styles.results}>
                  <NavigableList>
                    {[...(results.worldwide || []), ...(results.typeaheadjson || [])].map(
                      r => (isActive, onMouseMove) => (
                        <TypeaheadJsonResult
                          result={r}
                          tracker={tracker}
                          onMouseMove={onMouseMove}
                          onSelection={onSelection}
                          active={isActive}
                          key={`result=${r.value}`}
                          includeIcon
                        />
                      ),
                    )}
                  </NavigableList>
                </div>
              )}
            </Selector>
          </div>

          <CommentBox
            className={styles.commentSpace}
            value={this.props.comment}
            onChange={this.props.onCommentChange}
            minLength={10}
            maxLength={250}
          />
        </div>
        <div
          style={{
            display: this.state.locationKnown === LocationKnownValues.no ? 'block' : 'none',
          }}
        >
          <CommentBox
            className={styles.commentSpace}
            value={this.props.comment}
            onChange={this.props.onCommentChange}
            minLength={10}
            maxLength={250}
            required
            label={localize('social_report_level3_2')}
          />
        </div>
      </div>
    );
  };
}

export default SuggestLocationBox;
